import {
  useBrokersServiceBrokersBrokersDestroy,
  useBrokersServiceBrokersBrokersListKey,
  useBrokersServiceBrokersBrokersReInviteAdminCreate
} from '@insurance/api/queries'
import { BrokerList } from '@insurance/api/requests'
import { BrokerStatus } from '@insurance/types'
import { useInvalidateQueries } from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import { App, MenuProps, Table, TableColumnsType, TableProps } from 'antd'
import { SorterResult } from 'antd/es/table/interface'
import { DirectboxSend, Edit2, ProfileCircle, Trash } from 'iconsax-react'
import { useTranslation } from 'react-i18next'

import { getTableColumns } from './tableColumns'

type Props = {
  data?: BrokerList[]
  onNameClick?: (broker: BrokerList) => void
  triggerSort: (sorter: SorterResult<BrokerList>) => void
  getSortedTableColumns: (
    columns: TableColumnsType<BrokerList>
  ) => TableColumnsType<BrokerList>
  onEdit: (id: number) => void
  onChangeAdmin: (id: number) => void
} & TableProps<BrokerList>

export const CompaniesManagementTable = ({
  data,
  onNameClick,
  triggerSort,
  getSortedTableColumns,
  onEdit,
  onChangeAdmin,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const { invalidateQueries } = useInvalidateQueries()
  const { modal, message } = App.useApp()

  const getActionColumnMenu: (content: BrokerList) => MenuProps['items'] = (
    content
  ) => [
    {
      label: t('edit'),
      key: 'edit',
      icon: <Edit2 size={16} />,
      onClick: () => onEdit(Number(content?.id))
    },

    {
      label: t('change_admin'),
      key: 'change_admin',
      icon: <ProfileCircle size={16} />,
      onClick: () => onChangeAdmin(Number(content?.id))
    },

    ...(content.status === BrokerStatus.PENDING_EMAIL
      ? [
          {
            label: t('re_invite'),
            key: 're_invite',
            icon: <DirectboxSend size={16} />,
            onClick: () => reInviteConfirm(Number(content?.id))
          }
        ]
      : []),

    ...((content.status === BrokerStatus.DRAFT
      ? [
          {
            type: 'divider'
          },
          {
            label: t('delete'),
            key: 'delete',
            icon: <Trash size={16} />,
            danger: true,
            onClick: () => deleteConfirm(Number(content?.id))
          }
        ]
      : []) as Required<MenuProps>['items'])
  ]

  const deleteConfirm = (id: number) => {
    modal.confirm({
      title: t('delete_confirm'),
      content: t('delete_confirm_description_company'),
      okText: t('delete'),
      cancelText: t('cancel'),
      okType: 'danger',
      onOk: () => deleteMutation({ id: id as number })
    })
  }

  const reInviteConfirm = (id: number) => {
    modal.confirm({
      title: t('re_invite_confirm'),
      content: t('re_invite_confirm_description'),
      okText: t('re_invite'),
      cancelText: t('cancel'),
      onOk: () => reInviteMutation({ id: id as number })
    })
  }

  const { mutateAsync: deleteMutation } =
    useBrokersServiceBrokersBrokersDestroy({
      onSuccess: () => {
        message.success(t('company_deleted_successfully'))
        invalidateQueries([useBrokersServiceBrokersBrokersListKey])
      },
      onError: (error) => showApiErrors(error)
    })

  const { mutateAsync: reInviteMutation } =
    useBrokersServiceBrokersBrokersReInviteAdminCreate({
      onSuccess: () => {
        message.success(t('re_invited_successfully'))
        invalidateQueries([useBrokersServiceBrokersBrokersListKey])
      },
      onError: (error) => showApiErrors(error)
    })

  const columns = getTableColumns(getActionColumnMenu, t, { onNameClick })

  const sortedColumns = getSortedTableColumns(columns)

  return (
    <Table
      columns={sortedColumns}
      dataSource={data}
      rowKey="id"
      scroll={{ x: 'max-content' }}
      onChange={(_, __, sorter) =>
        triggerSort(Array.isArray(sorter) ? sorter[0] : sorter)
      }
      {...props}
    />
  )
}
